import {
	cloneDeep, min, set, startCase, uniq,
} from 'lodash';
import {
	CellEditorDataTransformer,
	ColumnModeTypeEnum,
	focusSuperGridCell,
	getLevelByDepth,
	getLevelDepth,
	insertAddColumns,
	MutationsType,
	NumberedAnchorsBody,
	NumberedAnchorsContainer,
	refocusCell,
	SuperGridVerticalAnchors,
	TransformedVerticalSuperGridColDef,
	useColumnEditor,
	useSupergridMutations,
	useSuperGridValidate,
	useSupergridVerticalEngine,
	verticalParentNameColSpan,
	VerticalSuperGridColDef,
} from '@aleph/supergrid';
import React, {
	useEffect, useMemo, useState,
} from 'react';
import { SPOTIFY_SUPERGRID_CONFIG } from 'wb-spotify/app/routes/superGrid/config';
import {
	SPOTIFY_CROSS_LEVEL_DEPENDENCIES,
	SpotifyEntityCreateStatus,
	SpotifySuperGridMutationsConfig,
} from 'wb-spotify/app/routes/superGrid/mutator/utils';
import classNames from 'classnames';
import SpotifyVerticalAddCellRenderer from 'wb-spotify/app/routes/superGrid/mutator/vertical/cellRenderer/add';
import { AnimatePresence, motion } from 'framer-motion';
import { useApolloClient } from '@apollo/client';
import SpotifyCellRendererWrapper from 'wb-spotify/app/routes/superGrid/cellRenderers/wrapper';
import { groupHeaderNames } from 'wb-spotify/app/routes/superGrid/columnDefinitions/headerNames';
import {
	ArrowLeft,
	DirectionRight,
	processValueFromJSON,
	processValueToJSON,
	updateAndReturnAffectedChildren,
	Button, ButtonAppearance, ButtonColor, Floaty, Tooltip, TrashCanIcon, useUrlParams,
	ButtonSize,
	DefaultCellRenderer,
} from '@aleph/shared-components';
import { preValidateSpotifyEntities, validateSpotifyEntity } from 'wb-spotify/app/routes/superGrid/validation/utils';
import useMutationsUndoRedoPlugin from 'wb-spotify/app/routes/superGrid/hooks/useMutationsUndoRedoPlugin';
import {
	ColumnApi,
	GridApi,
	CellClassParams,
	GridReadyEvent,
	CellValueChangedEvent,
	CellKeyDownEvent,
	RowHeightParams,
	CellEditingStartedEvent,
	FillOperationParams,
} from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import useInstantEditPlugin from 'wb-spotify/app/routes/superGrid/hooks/useInstantEditPlugin';
import NameEditor from 'wb-reddit/src/app/common/components/autoNaming/autoNamingEditor';
import { SpotifyLevel, MAPPED_COLUMN_NAMES } from 'wb-spotify/app/routes/superGrid/utils';
import { editableOnThisLevel } from 'wb-spotify/app/routes/superGrid/helpers';

import '@aleph/shared-components/scss/supergrid-mutator-vertical-grid.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import VerticalNameCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/name/verticalNameCellRenderer';
import { ValidationNavigator } from 'wb-spotify/app/common/supergridValidation';
import useSaveBeforeExitDialog from 'wb-reddit/app/routes/hooks/useSaveBeforeExitDialog';
import usePublishButtonVisibility from 'wb-spotify/app/routes/superGrid/mutator/hooks/usePublishButtonVisibility';

LicenseManager.setLicenseKey(
	// eslint-disable-next-line max-len
	'Using_this_AG_Grid_Enterprise_key_( AG-043155 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Httpool d.o.o. )_is_granted_a_( Multiple Applications )_Developer_License_for_( 3 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 1 July 2024 )____[v2]_MTcxOTc4ODQwMDAwMA==3f4d73dbd0b55114fae1030bca4daf44',
);

const DEFAULT_VERTICAL_COL_DEF = {
	width:     375,
	cellStyle: ({
		borderRightColor: 'hsl(var(--gray-300))',
		padding:          0,
	}),
};

const campaignVerticalNameColDef: TransformedVerticalSuperGridColDef<SpotifyLevel> = {
	field:            'campaignVerticalName',
	fieldName:        'Campaign',
	level:            SpotifyLevel.GROUP,
	columnMode:       ColumnModeTypeEnum.VERTICAL,
	cellRenderer:     DefaultCellRenderer,
	valueGetter:      params => params?.data.parent?.name,
	colSpan:          verticalParentNameColSpan,
	columnOrderIndex: -2,
};

const groupVerticalNameColDef: TransformedVerticalSuperGridColDef<SpotifyLevel> = {
	field:            'groupVerticalName',
	fieldName:        'Group',
	headerName:       groupHeaderNames.groupName,
	level:            SpotifyLevel.AD,
	columnMode:       ColumnModeTypeEnum.VERTICAL,
	cellRenderer:     DefaultCellRenderer,
	valueGetter:      params => params?.data.parent?.name,
	colSpan:          verticalParentNameColSpan,
	columnOrderIndex: -2,
};

const getVerticalLevel = (mutations: MutationsType<SpotifySuperGridMutationsConfig>) => {
	if (mutations[SpotifyLevel.CAMPAIGN]?.some(campaign => !campaign?.id)) {
		return SpotifyLevel.CAMPAIGN;
	}

	if (mutations[SpotifyLevel.GROUP]?.some(group => !group?.id)) {
		return SpotifyLevel.GROUP;
	}

	return SpotifyLevel.AD;
};

const SpotifyVerticalMutationGrid = ({ confirmMutations }) => {
	const {
		mutations: storeMutations,
		changeMutation,
		getMutationsWithParents,
		clearMutations,
	} = useSupergridMutations<SpotifySuperGridMutationsConfig>();
	const navigate = useNavigate();

	const { params: urlParams, setUrlParam } = useUrlParams({
		navigate,
		useParams,
	});
	const [touchedLevels, setTouchedLevels] = useState<SpotifyLevel[]>([]);
	const [visitedLevels, setVisitedLevels] = useState<SpotifyLevel[]>([]);

	const mutations = useMemo(() => getMutationsWithParents(SPOTIFY_SUPERGRID_CONFIG), [storeMutations]);

	const { context: { setValidations } } = useSuperGridValidate();

	const client = useApolloClient();

	const { t } = useTranslation();

	const level = urlParams.creationLevel ?? getVerticalLevel(mutations);

	useEffect(() => {
		setVisitedLevels(uniq([...visitedLevels, level]));
	}, [level]);

	useEffect(() => {
		/**
		 * Determine which levels should be validated based on visited and touched levels
		 *
		 * We validate a level if:
		 * 1. User has 'touched' the level ie. changed the value of a cell
		 * 2. User has moved to the next level ie. clicked the next step button, or in the anchors
		 * 3. For the current level, only validate if touched or after submit ie. on ad level
		 */
		const getLevelsToValidate = () => {
			const levelsToValidate: SpotifyLevel[] = [];

			levelsToValidate.push(...touchedLevels);

			const levelHierarchy = [SpotifyLevel.CAMPAIGN, SpotifyLevel.GROUP, SpotifyLevel.AD];

			for (let i = 0; i < levelHierarchy.length; i++) {
				const currentLevel = levelHierarchy[i];

				if (!touchedLevels.includes(currentLevel)) {
					const nextLevel = levelHierarchy[i + 1];

					if (nextLevel && visitedLevels.includes(nextLevel)) {
						levelsToValidate.push(currentLevel);
					}
				}
			}

			return [...new Set(levelsToValidate)];
		};

		const levelsToValidate = getLevelsToValidate();

		preValidateSpotifyEntities({
			campaign: levelsToValidate.includes(SpotifyLevel.CAMPAIGN) ? mutations.campaign : [],
			group:    levelsToValidate.includes(SpotifyLevel.GROUP) ? mutations.group : [],
			ad:       levelsToValidate.includes(SpotifyLevel.AD) ? mutations.ad : [],
		}).then(errors => setValidations(errors));
	}, [storeMutations, JSON.stringify(touchedLevels), JSON.stringify(visitedLevels)]);

	const setLevel = (newLevel) => {
		setUrlParam('creationLevel', newLevel);
	};

	const [showHeader, setShowHeader] = useState<boolean>(true);
	const [colApi, setColApi] = useState<ColumnApi | null>(null);
	const [api, setApi] = useState<GridApi | null>(null);

	const { onCellEditingStarted: onCellEditingStartedColumnEditor, onCellEditingStopped } = useColumnEditor(
		{
			gridApi:  api,
			vertical: true,
		},
	);

	const { context } = useSuperGridValidate({
		validate:            validateSpotifyEntity,
		entityHasValidation: (v, e) => v.id === e.data?.id || v.id === e.data?.hash,
		gridApi:             api,
	});

	const validations = context?.validations ?? [];

	const onAnchorLevelChange = (newLevel: SpotifyLevel) => {
		api?.stopEditing();

		setLevel(newLevel);
	};

	useEffect(() => {
		onCellEditingStopped();
	}, [level]);

	const onPreviousLevel = () => {
		api?.stopEditing();

		switch (level) {
			case SpotifyLevel.GROUP:
				setLevel(SpotifyLevel.CAMPAIGN);

				break;
			case SpotifyLevel.AD:
				setLevel(SpotifyLevel.GROUP);

				break;
		}
	};

	const onNextLevel = async () => {
		api?.stopEditing();

		switch (level) {
			case SpotifyLevel.CAMPAIGN:
				setLevel(SpotifyLevel.GROUP);

				break;
			case SpotifyLevel.GROUP:
				setLevel(SpotifyLevel.AD);

				break;
			case SpotifyLevel.AD:
				confirmMutations(api);
		}
	};

	const config = useMemo(() => ({
		...SPOTIFY_SUPERGRID_CONFIG,
		cellRendererWrapper: SpotifyCellRendererWrapper,
		cellEditorWrapper:   CellEditorDataTransformer,
	}), []);

	const colDefs = useMemo(() => ([
		campaignVerticalNameColDef,
		groupVerticalNameColDef,
		{
			field:              'name',
			colId:              `${level}Name`,
			headerName:         groupHeaderNames.groupName,
			cellRenderer:       SpotifyCellRendererWrapper,
			cellEditor:         NameEditor,
			cellRendererParams: {
				originalRenderer: VerticalNameCellRenderer,
				field:            `${level}Name`,
			},
			editable: editableOnThisLevel,
			level,
		},
	]), [level]);

	const fieldColumn = useMemo(() => (
		{
			cellStyle: (params: CellClassParams) => ({
				borderRightColor: params.data?.isFullRow ? 'transparent' : '',
				fontWeight:       params.data?.isFullRow ? 'bold' : '',
				color:            '#999999',
			}),
		}
	), []);

	const {
		onCellValueChanged: onCellValueChangedUndoRedo,
		onUndoStarted,
		onRedoStarted,
	} = useMutationsUndoRedoPlugin({ gridApi: api });

	const { onCellEditingStarted: onInstantEdit } = useInstantEditPlugin();

	const { columnDefs, rowData: transposedData } = useSupergridVerticalEngine({
		config,
		data:       mutations,
		addNameRow: false,
		level,
		fieldColumn,
		colDefs,
	});

	const parentMutations = mutations[
		getLevelByDepth(SPOTIFY_SUPERGRID_CONFIG, getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, level) - 1)
	];

	const verticalColDefs = useMemo(() => insertAddColumns(
		columnDefs as VerticalSuperGridColDef<SpotifyLevel>[],
		mutations[level],
		parentMutations,
		level,
		SpotifyVerticalAddCellRenderer,
	), [columnDefs, level]);

	const [rowData, pinnedTopRowData] = useMemo(() => [
		transposedData.slice(level === SpotifyLevel.AD ? 2 : (level === SpotifyLevel.CAMPAIGN ? 0 : 1)),
		transposedData.slice(0, level === SpotifyLevel.AD ? 2 : (level === SpotifyLevel.CAMPAIGN ? 0 : 1)),
	], [transposedData]);

	const onGridReady = (e: GridReadyEvent) => {
		setColApi(e.columnApi);
		setApi(e.api);

		const firstColToBeFocused = e.columnApi?.getAllDisplayedColumns()?.[1]?.getColId();

		if (firstColToBeFocused) {
			focusSuperGridCell({ api: e.api, rowIndex: 0, field: firstColToBeFocused });
		}
	};

	const gridWidth = (() => {
		if (colApi) {
			return colApi.getAllDisplayedColumns()?.reduce(
				(accu, colDef) => accu + (colDef.getActualWidth() ?? 375),
				0,
			);
		}

		return 0;
	})();

	const onCellEditingStarted = (e: CellEditingStartedEvent) => {
		onCellEditingStartedColumnEditor(e);
		onInstantEdit(e);
	};

	const onCellValueChanged = async (e: CellValueChangedEvent) => {
		setTouchedLevels(curr => [...curr, e.data?.level]);

		const entityData = (e.colDef as VerticalSuperGridColDef)?.entityData ?? e.data;

		onCellValueChangedUndoRedo?.(e);

		const entityKey = {
			id:   entityData?.id,
			hash: entityData?.hash,
		};

		const updatedData = e.data?.field
			? set(cloneDeep(entityData), e.data.field, e.newValue)
			: entityData;

		const newData = {
			...entityKey,
			...updatedData,
		};

		changeMutation(newData);

		const changedField = e.data?.colId ?? e.data?.field ?? '';

		const dependencies = SPOTIFY_CROSS_LEVEL_DEPENDENCIES[changedField]?.(e.newValue);
		const affectedChildrenHashes = updateAndReturnAffectedChildren({
			parentHash: entityKey?.hash,
			dependencies,
		}).map(child => child.hash);

		const dependenciesKeys = Object.keys(dependencies ?? {});

		if (changedField === 'campaignObjective') {
			const affectedGroups = mutations.group.filter(
				item => item.parent.hash === entityKey?.hash,
			).map(item => item.hash as string);

			affectedChildrenHashes.push(...affectedGroups);
			dependenciesKeys.push('groupBidAmount');
		}

		context.unsetValidations(dependenciesKeys, affectedChildrenHashes);
	};

	const hasValidationErrors = validations?.some(validation => validation.level === level);

	useEffect(() => {
		api?.refreshCells({ force: true });
	}, [validations]);

	const onCellKeyDown = (e: CellKeyDownEvent) => {
		if ((e.event as KeyboardEvent).key === 'Escape') {
			refocusCell(e);
		}
	};

	const isInvalid = hasValidationErrors;

	const gridOptions = useMemo(() => ({
		fillOperation: (params: FillOperationParams) => {
			const namingExpression = (params.api.getCellRanges()?.[0]?.startColumn?.getColDef() as VerticalSuperGridColDef)?.entityData?.namingExpression;

			return namingExpression || params.values[0];
		},
	}), []);

	const {
		dialog: saveBeforeExitModal,
		beforeClose,
	} = useSaveBeforeExitDialog({
		onConfirm:                  clearMutations,
		defaultShouldPromptOnClose: true,
	});

	const showPublishButton = usePublishButtonVisibility(level);

	return (
		<>
			<ValidationNavigator
				isVertical
				gridApi={api}
				setLevel={setLevel}
				level={level}
				mappedFields={MAPPED_COLUMN_NAMES}
				initialHidden
			/>

			<SuperGridVerticalAnchors
				config={SPOTIFY_SUPERGRID_CONFIG}
				topLevel={SpotifyLevel.CAMPAIGN}
				level={level}
				setLevel={onAnchorLevelChange}
				components={{
					anchorsContainer: NumberedAnchorsContainer,
					anchorBody:       (props) => (<NumberedAnchorsBody
						{...props}
						components={{
							anchorMain: (mainProps) => (<button {...mainProps.componentProps}
								className={classNames(mainProps.componentProps.className, 'font-bold')}
								data-testid={`anchor-${mainProps.componentProps.key}-button`}
							>
								{mainProps.children}
							</button>),
							anchorLabel: (labelProps) => (<div {...labelProps.componentProps}
								className='text-[12px]'
							>
								{labelProps.children}
							</div>),
						}}
					/>),
				}}
			/>

			<AnimatePresence mode="wait">
				<div
					key={level}
					className="mutator-grid ag-theme-alpine
					flex flex-row flex-1 justify-center overflow-y-auto mr-[-32px]"
				>
					<motion.div
						id="super-grid"
						transition={{ duration: 0.1 }}
						exit={{ filter: 'blur(10px)' }}
						initial={{ filter: 'blur(10px)' }}
						animate={{ filter: 'blur(0px)' }}
						className={classNames('flex-col flex', {
							'vertical-grid-scrolled': !showHeader,
						})}
						style={{ flexBasis: gridWidth }}
						onWheel={e => {
							const { target } = e;

							if (target instanceof Element) {
								/**
								 * Traverse the DOM to check if the target is in a select menu or column editor
								 * If it is, do not stop editing, otherwise stop ag grid edit mode
								 */
								// TODO: When we switch to our own dropdown/menu need to change the check here
								const isInSelectMenu = target.closest('[id*="react-select"]') !== null;
								const isInColumnEditor = target.closest('[id*="column-editor-wrapper"]') !== null;

								if (!isInSelectMenu && !isInColumnEditor) {
									api?.stopEditing();

									if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
										setShowHeader(e.deltaY < 0);
									}
								}
							}
						}}
					>
						<div
							className={classNames('text-[24px] flex items-center py-[36px] transition-all', {
								'max-h-0 !p-0': !showHeader,
							})}
						>
							{startCase(level)} Settings
						</div>

						<AgGridReact
							onUndoStarted={onUndoStarted}
							onRedoStarted={onRedoStarted}
							undoRedoCellEditing
							containerStyle={{
								// TODO: This has to be re-rendered when user changes window width
								marginRight: min([0, 16 - ((document.body.clientWidth - gridWidth) / 2)]),
							}}
							className="vertical-mutation-grid"
							suppressContextMenu
							enableRangeSelection
							suppressScrollOnNewData
							onGridReady={onGridReady}
							onCellValueChanged={onCellValueChanged}
							onCellKeyDown={onCellKeyDown}
							onCellFocused={e => e.api?.refreshCells({ force: true })}
							enableFillHandle
							headerHeight={0}
							rowClassRules={{
								'bg-white border-l border-l-gray-300 border-r border-r-gray-300': () => true,
								'border-t border-t-gray-300':
									(row) => row.rowIndex === 0,
								'!bg-gray-50 !text-[14px] !text-gray-600':
                                    params => params.data?.field === 'groupVerticalName'
                                        || params.data?.field === 'campaignVerticalName',
							}}
							rowBuffer={100}
							rowHeight={40}
							onRowDataUpdated={refocusCell}
							suppressColumnVirtualisation
							suppressRowTransform
							suppressPropertyNamesCheck
							rowData={rowData}
							context={{
								...context, client, isVertical: true, isInCreation: true,
							}}
							onCellEditingStarted={onCellEditingStarted}
							onCellEditingStopped={onCellEditingStopped}
							pinnedTopRowData={pinnedTopRowData}
							processCellFromClipboard={processValueFromJSON}
							processCellForClipboard={processValueToJSON}
							columnDefs={verticalColDefs}
							defaultColDef={DEFAULT_VERTICAL_COL_DEF}
							getRowHeight={(params: RowHeightParams) => (params.data?.field === 'adPreview' ? 250 : 40)}
							gridOptions={gridOptions}
						/>
					</motion.div>

					<Floaty>
						<Button
							onClick={beforeClose}
							leftNode={
								<TrashCanIcon />
							}
							appearance={ButtonAppearance.OUTLINED}
							color={ButtonColor.RED}
							data-testid="discard-changes-button"
							size={ButtonSize.LARGE}
						/>

						{level !== SpotifyLevel.CAMPAIGN
							&& (
								<Button
									data-testid="back-button"
									appearance={ButtonAppearance.OUTLINED}
									leftNode={<ArrowLeft />}
									onClick={onPreviousLevel}
									size={ButtonSize.LARGE}
								/>
							)
						}
						<Tooltip
							placement="top"
							content={t('sc: supergrid creation floaty there are errors message', 'There are errors')}
							disabled={validations.length === 0}
						>
							<Button
								title="Save draft"
								data-testid="save-draft-button"
								onClick={() => confirmMutations(api, SpotifyEntityCreateStatus.DRAFT)}
								disabled={validations.length > 0}
								appearance={ButtonAppearance.OUTLINED}
								style={{ pointerEvents: hasValidationErrors ? 'none' : undefined }}
								size={ButtonSize.LARGE}
							/>
						</Tooltip>

						{
							showPublishButton && (
								<Tooltip
									placement="top"
									content={t('sc: supergrid creation floaty there are errors message', 'There are errors')}
									disabled={!isInvalid}
									delay={300}
									singleLine
								>
									<Button
										onClick={onNextLevel}
										style={{ pointerEvents: isInvalid ? 'none' : undefined }}
										disabled={isInvalid}
										title={level !== SpotifyLevel.AD ? 'Next step' : 'Publish to Spotify'}
										data-testid={level !== SpotifyLevel.AD ? 'next-step-button' : 'publish-button'}
										rightNode={
											<DirectionRight />
										}
										size={ButtonSize.LARGE}
									/>
								</Tooltip>
							)
						}
					</Floaty>
				</div>
			</AnimatePresence>

			{saveBeforeExitModal}
		</>
	);
};

export default SpotifyVerticalMutationGrid;
